import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { SESSION_EXPIRED_EVENT } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { loginUrl } from "../../utils/returnPath";

/** App-wide notice when the owner session expires, with a login that returns to the same page. */
const SessionExpiredNotice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [returnTo, setReturnTo] = useState(null);

  useEffect(() => {
    const onExpired = () => {
      const path = window.location.pathname + window.location.search;
      setReturnTo(path.startsWith("/?login") || path === "/" ? "/profile" : path);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    if (returnTo && location.search.includes("login=1")) setReturnTo(null);
  }, [location.search, returnTo]);

  if (!returnTo) return null;

  return (
    <div
      role="alert"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[200] w-[calc(100%-2rem)] max-w-md rounded-xl border border-border bg-card shadow-lg p-4 flex items-start gap-3"
    >
      <div className="flex-1 min-w-0">
        <p className="m-0 font-semibold text-foreground">{t("session.expiredTitle")}</p>
        <p className="m-0 mt-1 text-caption text-muted-foreground">{t("session.expiredBody")}</p>
        <button
          type="button"
          className="mt-3 text-sm font-semibold text-primary"
          onClick={() => {
            const next = returnTo;
            setReturnTo(null);
            navigate(loginUrl(next));
          }}
        >
          {t("session.loginAgain")}
        </button>
      </div>
      <button type="button" aria-label={t("session.dismiss")} className="text-muted-foreground" onClick={() => setReturnTo(null)}>
        <X size={18} />
      </button>
    </div>
  );
};

export default SessionExpiredNotice;
